import classnames from 'classnames';
import {
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type PointerEvent as ReactPointerEvent,
    type RefObject,
} from 'react';
import type { CharacterDisciplines, CharacterEquipmentLoadout, Skill, Vector2 } from '@sorrem/shared';
import type { UISkillTreeView } from '@sorrem-client/types';
import { useValueStoreEffect } from '@sorrem-client/ui/hooks';
import { centerSkillTreeView, panSkillTreeView, zoomSkillTreeViewAt, type SkillTreeViewStore } from './skill-tree-view';
import { buildSkillTreeModel } from './skill-tree-model';
import { SkillTreeEffectsLayer } from './SkillTreeEffectsLayer';
import { SkillTreeIntroClockContext } from './SkillTreeIntroPart';
import { SkillTreeScene } from './SkillTreeScene';
import { usePrefersReducedMotion } from '@sorrem-client/ui/hooks';
import { useSkillTreeIntro } from './use-skill-tree-intro';
import styles from './SkillTree.module.scss';

interface SkillTreeProps {
    /** The character id, which seeds their sigil and keys what they last saw (for the intro). */
    readonly characterId: string;
    /** The character's skills and equipment, which drive every node and dial. */
    readonly skills: readonly Skill[];
    readonly equipped: CharacterEquipmentLoadout;
    /** Veil Attunement and Martial Discipline, 0 to 100, which fill their dials (`UIPartyCharacter.disciplines`). */
    readonly disciplines: CharacterDisciplines;
    /** Weighted praxis by skill id over the last few runs (`resolveRecentTraining`), for the link pulses. */
    readonly recentTraining: Readonly<Record<string, number>>;
    /** The selected node, dial, or root (a skill id, or an id from `skill-tree-ids.ts`); its details show in the right panel. */
    readonly selectedId: string | null;
    /** Called with the clicked node or dial, or null when the player clicks open sky. */
    readonly onSelect: (id: string | null) => void;
    /** The pan/zoom shared with the backdrop (see `SkillTreeBackground`), so every layer stays aligned. */
    readonly viewStore: SkillTreeViewStore;
    /** Fades the tree in or out; a hidden tree ignores the pointer. */
    readonly isVisible: boolean;
    /** "Character Home effects" Minimal or All: when off, the effects layer stays hidden and still. */
    readonly animationsEnabled: boolean;
    /**
     * The part of the screen not covered by other UI (e.g. Character Home's
     * center panel). The tree opens centered on it.
     */
    readonly frameRegionRef?: RefObject<HTMLElement>;
    readonly className?: string;
    /** Called when the tree comes on screen or leaves it, which lags `visible` while it shrinks back into the sigil. */
    readonly onShownChange?: (isShown: boolean) => void;
}

/** Wheel zoom speed: the zoom multiplies by e^(-pixels x this) per wheel event. */
const WHEEL_ZOOM_RATE = 0.0015;
/** Pixels per wheel "line" for mice that report lines instead of pixels. */
const WHEEL_LINE_PIXELS = 16;
/** A press that moves less than this (CSS px) is a click, not a drag. */
const CLICK_SLOP = 4;

/** The node or dial id under a pointer event, from the hit target's `data-node-id`. */
function targetNodeId(target: EventTarget | null): string | null {
    if (!(target instanceof Element)) return null;
    return target.closest('[data-node-id]')?.getAttribute('data-node-id') ?? null;
}

/**
 * The Skills tab's constellation: a full-screen layer above the Veil sky and
 * below Character Home's panels, which float over it. Tree space is mapped
 * onto the screen by `view`: the tree point `view.center` sits at the
 * screen's center, at `view.zoom` CSS pixels per tree unit.
 *
 * Pan by dragging anywhere the panels don't cover (move cursor); zoom with
 * the mouse wheel toward the pointer. The zoom buttons live with the panels
 * (see `SkillsMainPanel`). Panning and zooming write the plane's transform
 * straight from the view store; they never re-render the tree.
 */
export const SkillTree = memo(function SkillTree({
    characterId,
    skills,
    equipped,
    disciplines,
    recentTraining,
    selectedId,
    animationsEnabled,
    onSelect,
    viewStore,
    isVisible,
    frameRegionRef,
    className,
    onShownChange,
}: SkillTreeProps): JSX.Element {
    const surfaceRef = useRef<HTMLDivElement>(null);
    const planeRef = useRef<HTMLDivElement>(null);
    const lastPointerRef = useRef<Vector2 | null>(null);
    /** Where the current press started and what it started on, to tell a click from a drag. */
    const pressRef = useRef<{ start: Vector2; nodeId: string | null; moved: boolean } | null>(null);
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const reducedMotion = usePrefersReducedMotion();
    const model = useMemo(
        () => buildSkillTreeModel(skills, equipped, disciplines, recentTraining),
        [skills, equipped, disciplines, recentTraining],
    );
    const motion = animationsEnabled && !reducedMotion;
    const animate = isVisible && motion;
    const intro = useSkillTreeIntro({ characterId, model, isVisible, isMotionAllowed: motion });

    useEffect(() => {
        onShownChange?.(intro.isShown);
    }, [onShownChange, intro.isShown]);
    const hasFramedRef = useRef(false);

    // Open at the default zoom, centered in the frame region, once; after that the player's pan/zoom is kept.
    useEffect(() => {
        const surface = surfaceRef.current;
        if (!surface || hasFramedRef.current) return;
        const frame = (): void => {
            if (hasFramedRef.current || surface.clientWidth === 0 || surface.clientHeight === 0) return;
            hasFramedRef.current = true;
            const surfaceRect = surface.getBoundingClientRect();
            const regionRect = frameRegionRef?.current?.getBoundingClientRect();
            const region = regionRect && regionRect.width > 0 && regionRect.height > 0
                ? {
                    left: regionRect.left - surfaceRect.left,
                    top: regionRect.top - surfaceRect.top,
                    width: regionRect.width,
                    height: regionRect.height,
                }
                : undefined;
            viewStore.set(centerSkillTreeView({ width: surfaceRect.width, height: surfaceRect.height }, region));
            // Framed once; the observer has nothing more to do.
            observer.disconnect();
        };
        // The layer may not have its size yet on the first render.
        const observer = new ResizeObserver(frame);
        frame();
        if (!hasFramedRef.current) {
            observer.observe(surface);
        }
        return () => observer.disconnect();
    }, [viewStore, frameRegionRef]);

    // Tree space onto the screen: the view's center at the screen's center, at its zoom.
    const placePlane = useCallback((view: UISkillTreeView): void => {
        if (planeRef.current) {
            planeRef.current.style.transform = `scale(${view.zoom}) translate(${-view.center.x}px, ${-view.center.y}px)`;
        }
    }, []);
    useValueStoreEffect(viewStore, placePlane);

    // Wheel zoom needs a non-passive listener to stop the page from scrolling.
    useEffect(() => {
        const surface = surfaceRef.current;
        if (!surface) return;

        const handleWheel = (event: WheelEvent): void => {
            event.preventDefault();
            const pixels = event.deltaMode === WheelEvent.DOM_DELTA_LINE
                ? event.deltaY * WHEEL_LINE_PIXELS
                : event.deltaMode === WheelEvent.DOM_DELTA_PAGE ? event.deltaY * surface.clientHeight : event.deltaY;
            const rect = surface.getBoundingClientRect();
            const focus = { x: event.clientX - (rect.left + rect.width / 2), y: event.clientY - (rect.top + rect.height / 2) };
            viewStore.set((current) => zoomSkillTreeViewAt(current, current.zoom * Math.exp(-pixels * WHEEL_ZOOM_RATE), focus));
        };

        surface.addEventListener('wheel', handleWheel, { passive: false });
        return () => surface.removeEventListener('wheel', handleWheel);
    }, [viewStore]);

    const { skip: skipIntro } = intro;
    const handlePointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>): void => {
        if (event.button !== 0) return;
        // A click on the tree skips the intro to the finished tree.
        skipIntro();
        // Read the target before capturing: capture retargets later events to the surface.
        pressRef.current = { start: { x: event.clientX, y: event.clientY }, nodeId: targetNodeId(event.target), moved: false };
        event.currentTarget.setPointerCapture(event.pointerId);
        lastPointerRef.current = { x: event.clientX, y: event.clientY };
    }, [skipIntro]);

    const handlePointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>): void => {
        const last = lastPointerRef.current;
        if (!last) {
            setHoveredId(targetNodeId(event.target));
            return;
        }
        const press = pressRef.current;
        if (press && !press.moved && Math.hypot(event.clientX - press.start.x, event.clientY - press.start.y) >= CLICK_SLOP) {
            press.moved = true;
        }
        const delta = { x: event.clientX - last.x, y: event.clientY - last.y };
        lastPointerRef.current = { x: event.clientX, y: event.clientY };
        viewStore.set((current) => panSkillTreeView(current, delta));
    }, [viewStore]);

    const handlePointerEnd = useCallback((event: ReactPointerEvent<HTMLDivElement>): void => {
        if (!lastPointerRef.current) return;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        lastPointerRef.current = null;
        const press = pressRef.current;
        pressRef.current = null;
        // A click (not a drag) selects what it landed on; on open sky it clears the selection.
        if (event.type === 'pointerup' && press && !press.moved) {
            onSelect(press.nodeId);
        }
    }, [onSelect]);

    const handlePointerLeave = useCallback((): void => {
        setHoveredId(null);
    }, []);

    return (
        <div
            className={classnames(
                styles.skillTree,
                isVisible && styles.visible,
                !isVisible && intro.isShown && styles.closing,
                motion && styles.motion,
                className,
            )}
        >
            <div
                ref={surfaceRef}
                className={styles.surface}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerEnd}
                onPointerCancel={handlePointerEnd}
                onPointerLeave={handlePointerLeave}
            >
                <div ref={planeRef} className={styles.plane}>
                    <SkillTreeIntroClockContext.Provider value={intro.clock}>
                        <SkillTreeScene
                            model={model}
                            sigilSeed={characterId}
                            hoveredId={hoveredId}
                            selectedId={selectedId}
                            isReducedMotion={reducedMotion}
                            intro={intro.schedule}
                        />
                    </SkillTreeIntroClockContext.Provider>
                </div>
            </div>
            <SkillTreeEffectsLayer model={model} viewStore={viewStore} isActive={animate} timing={intro.effects} />
        </div>
    );
});
